import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loader2, LayoutDashboard, Clock, ClipboardList, FileText, Palmtree } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import { SaldoCard } from "@/components/portal/portalShared";
import { MiJornadaCard, KpisMes, AvisosPanel, ProximosDias } from "@/components/portal/ResumenBlocks";
import { SaldoDesgloseDialog } from "@/components/portal/SaldoDesgloseDialog";
import { MisFichajes } from "@/components/portal/MisFichajes";
import { SolicitudesPanel } from "@/components/portal/SolicitudesPanel";
import { DocumentosTab } from "@/components/portal/DocumentosTab";

const TABS = [
  { key: "resumen", label: "Resumen", icon: LayoutDashboard, cap: null },
  { key: "fichajes", label: "Mis fichajes", icon: Clock, cap: "mis_fichajes" },
  { key: "solicitudes", label: "Solicitudes", icon: ClipboardList, cap: "correcciones" },
  { key: "documentos", label: "Documentos", icon: FileText, cap: "documentos" },
];

export default function RRHH() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "resumen";
  const setTab = (t) => setParams(t === "resumen" ? {} : { tab: t });

  const [resumen, setResumen] = useState(null);
  const [meta, setMeta] = useState(null);
  const [saldoDias, setSaldoDias] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desglose, setDesglose] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, m] = await Promise.all([api.get("/portal-empleado/resumen"), api.get("/rrhh/meta")]);
      setResumen(r.data); setMeta(m.data);
      if (r.data.capacidades?.ausencias) api.get("/rrhh/saldo").then((s) => setSaldoDias(s.data)).catch(() => {});
    } catch { toast.error("Error cargando el portal del empleado"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !resumen) return (
    <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…</div>
  );
  if (!resumen) return null;
  const caps = resumen.capacidades;
  const esExterno = caps.tipo === "externo";
  const tabs = TABS.filter((t) => !t.cap || caps[t.cap]);
  const tabActiva = tabs.some((t) => t.key === tab) ? tab : "resumen";

  return (
    <div className="p-3 sm:p-4 lg:p-6 max-w-6xl" data-testid="rrhh-page">
      <PageHeader
        title={esExterno ? "Mi jornada" : "Portal del empleado"}
        subtitle={esExterno ? "Fichajes y trabajos" : `Hola ${user?.name?.split(" ")[0] || ""} · tu RRHH en un vistazo`}
      />

      <div className="flex gap-1 mb-4 border-b border-zinc-800 overflow-x-auto" data-testid="portal-tabs">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tabActiva === t.key ? "border-amber-400 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tabActiva === "resumen" && (
        <div className="space-y-4" data-testid="tab-resumen-content">
          <MiJornadaCard estado={resumen.estado} />
          <AvisosPanel avisos={resumen.avisos} />
          {caps.mis_fichajes && (
            <KpisMes mes={resumen.mes} saldo={caps.banco_horas ? resumen.saldo : null} onDesglose={caps.banco_horas ? () => setDesglose(true) : null} />
          )}
          {saldoDias && caps.ausencias && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="saldo-cards">
              <SaldoCard label="Vacaciones restantes" value={`${saldoDias.vacaciones_restantes} días`} sub={`${saldoDias.vacaciones_usadas} usados de ${saldoDias.vacaciones_total}`} color="emerald" icon={Palmtree} testid="saldo-vacaciones" />
              <SaldoCard label="Asuntos propios" value={`${saldoDias.asuntos_propios_restantes} días`} sub={`${saldoDias.asuntos_propios_usados} usados de ${saldoDias.asuntos_propios_total}`} color="sky" testid="saldo-aapp" />
              <SaldoCard label="Arrastre año anterior" value={`${saldoDias.arrastre_vacaciones} días`} color="zinc" sub={`Año ${saldoDias.year}`} />
            </div>
          )}
          <ProximosDias items={resumen.proximos} />
          {!esExterno && (
            <div className="text-[10px] text-zinc-600 px-1">
              Datos calculados por Control Horario 2.0 (fecha laboral Europe/Madrid). Horas acumuladas = periodos cerrados por administración; el mes en curso es provisional.
            </div>
          )}
        </div>
      )}
      {tabActiva === "fichajes" && <MisFichajes periodoInicial={resumen.periodo} onCambio={load} />}
      {tabActiva === "solicitudes" && <SolicitudesPanel caps={caps} saldo={resumen.saldo} meta={meta} onCambio={load} />}
      {tabActiva === "documentos" && <DocumentosTab caps={caps} />}

      <SaldoDesgloseDialog open={desglose} onClose={() => setDesglose(false)} />
    </div>
  );
}
